import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { 
  BarChart3, Users, TrendingUp, FileText, Clock, Award,
  ChevronRight, Download, Eye, RefreshCw
} from "lucide-react"

const UserDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null)
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [agentPerformance, setAgentPerformance] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDashboardData()
  }, [])

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await fetch("https://call-analyzer-backend.onrender.com/user/dashboard", {
        headers: { Authorization: `Bearer ${token}` }
      })
      
      if (response.ok) {
        const data = await response.json()
        setDashboardData(data)
      }
    } catch (error) {
      console.error("Failed to fetch dashboard:", error)
    } finally {
      setLoading(false)
    }
  }

  const fetchAgentPerformance = async (agentName) => {
    try {
      const token = localStorage.getItem("token")
      const response = await fetch(
        `https://call-analyzer-backend.onrender.com/user/agent-performance/${encodeURIComponent(agentName)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      if (response.ok) {
        const data = await response.json()
        setAgentPerformance(data)
        setSelectedAgent(agentName)
      }
    } catch (error) {
      console.error("Failed to fetch agent performance:", error)
    }
  }

  const viewAnalysisDetails = async (analysisId) => {
    try {
      const token = localStorage.getItem("token")
      const response = await fetch(
        `https://call-analyzer-backend.onrender.com/user/analysis/${analysisId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      
      if (response.ok) {
        const data = await response.json()
        // Open in modal or new page
        console.log("Analysis details:", data)
      }
    } catch (error) {
      console.error("Failed to fetch analysis:", error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-emerald-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Dashboard</h1>
          <p className="text-gray-600 mt-2">Track your call analysis performance</p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Analyses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboardData?.total_analyses || 0}
                </p>
              </div>
              <BarChart3 className="h-8 w-8 text-emerald-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">This Month</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboardData?.monthly_analyses || 0} / {dashboardData?.analyses_limit || 0}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Agents Tracked</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboardData?.agents?.length || 0}
                </p>
              </div>
              <Users className="h-8 w-8 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Score</p>
                <p className="text-2xl font-bold text-gray-900">
                  {dashboardData?.agents?.length > 0
                    ? (dashboardData.agents.reduce((acc, a) => acc + a.average_score, 0) / dashboardData.agents.length).toFixed(1)
                    : "0.0"}
                </p>
              </div>
              <Award className="h-8 w-8 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Recent Analyses */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">Recent Analyses</h2>
              <Link to="/analyzer" className="text-emerald-600 hover:text-emerald-700">
                New Analysis <ChevronRight className="inline h-4 w-4" />
              </Link>
            </div>
            
            <div className="space-y-3">
              {dashboardData?.recent_analyses?.map((analysis) => (
                <div key={analysis.id} className="border rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-gray-900">{analysis.agent_name}</p>
                      <p className="text-sm text-gray-600">
                        {analysis.call_type} • {analysis.industry}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(analysis.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        analysis.score >= 8 ? 'bg-green-100 text-green-800' :
                        analysis.score >= 6 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {analysis.score}/10
                      </span>
                      <button
                        onClick={() => viewAnalysisDetails(analysis.id)}
                        className="p-1 hover:bg-gray-200 rounded"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Agent Performance */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Agent Performance</h2>
            
            <div className="space-y-3">
              {dashboardData?.agents?.map((agent) => (
                <div 
                  key={agent.name}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => fetchAgentPerformance(agent.name)}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-semibold text-gray-900">{agent.name}</p>
                      <p className="text-sm text-gray-600">
                        {agent.total_calls} calls analyzed
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-gray-900">
                        {agent.average_score}
                      </p>
                      <p className="text-xs text-gray-600">avg score</p>
                    </div>
                  </div>
                  
                  {/* Mini progress bar */}
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-emerald-600 h-2 rounded-full"
                      style={{ width: `${(agent.average_score / 10) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Agent Performance Detail Modal/Section */}
        {agentPerformance && (
          <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">
                {agentPerformance.agent_name} - Performance History
              </h2>
              <button 
                onClick={() => setAgentPerformance(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="text-center">
                <p className="text-sm text-gray-600">Total Calls</p>
                <p className="text-2xl font-bold">{agentPerformance.total_calls}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Average Score</p>
                <p className="text-2xl font-bold">{agentPerformance.average_score}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-gray-600">Improvement</p>
                <p className={`text-2xl font-bold ${
                  agentPerformance.improvement > 0 ? 'text-green-600' : 
                  agentPerformance.improvement < 0 ? 'text-red-600' : 
                  'text-gray-600'
                }`}>
                  {agentPerformance.improvement > 0 ? '+' : ''}{agentPerformance.improvement}
                </p>
              </div>
            </div>
            
            {/* Performance Chart (simplified) */}
            <div className="space-y-2">
              {agentPerformance.performance_history.map((perf, idx) => (
                <div key={perf.id} className="flex items-center gap-4">
                  <span className="text-xs text-gray-600 w-24">
                    {new Date(perf.date).toLocaleDateString()}
                  </span>
                  <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                    <div 
                      className={`h-4 rounded-full ${
                        perf.score >= 8 ? 'bg-green-500' :
                        perf.score >= 6 ? 'bg-yellow-500' :
                        'bg-red-500'
                      }`}
                      style={{ width: `${(perf.score / 10) * 100}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-semibold w-12">
                    {perf.score}/10
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Link to="/analyzer" className="bg-emerald-600 text-white rounded-xl p-6 hover:bg-emerald-700 transition">
            <FileText className="h-8 w-8 mb-3" />
            <h3 className="font-semibold text-lg">New Analysis</h3>
            <p className="text-emerald-100 text-sm mt-1">Upload and analyze a new call</p>
          </Link>
          
          <Link to="/transcriptions" className="bg-blue-600 text-white rounded-xl p-6 hover:bg-blue-700 transition">
            <FileText className="h-8 w-8 mb-3" />
            <h3 className="font-semibold text-lg">View Transcriptions</h3>
            <p className="text-blue-100 text-sm mt-1">Access all your call transcripts</p>
          </Link>
          
          <Link to="/agent-reports" className="bg-purple-600 text-white rounded-xl p-6 hover:bg-purple-700 transition">
            <TrendingUp className="h-8 w-8 mb-3" />
            <h3 className="font-semibold text-lg">Analysis History</h3>
            <p className="text-purple-100 text-sm mt-1">View all past analyses & reports</p>
          </Link>
        </div>
      </div>
    </div>
  )
}

export default UserDashboard
